import { getCalendarioAction } from "@/modules/Events/service/EventsService"
import { CalendarioMeta, EventoDataType } from "@/types/events"
import { useEffect, useState } from "react"
import { useLoading } from "@/hooks/useLoading"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { EventCard } from "@/modules/Events/components/EventCard"
import { useToast } from "@/contexts/ToastContext"
import { motion } from "framer-motion"
import { SubscriptionsByEventFilter } from "./SubscriptionsByEventFilter"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"

const PAGE_SIZE = 10

function toAPIDate(d: Date) {
  return d.toISOString().slice(0, 10)
}

function apiDateToDisplay(dateStr: string): string {
  if (!dateStr || dateStr.length !== 10) return ""
  const [year, month, day] = dateStr.split("-")
  return `${day}/${month}/${year}`
}

function getPeriodDates(periodFilter: string, startDateStr: string, endDateStr: string) {
  const now = new Date()
  if (periodFilter === "este-mes") {
    return {
      data_ini: toAPIDate(new Date(now.getFullYear(), now.getMonth(), 1)),
      data_fim: toAPIDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    }
  }
  if (periodFilter === "proximo-mes") {
    return {
      data_ini: toAPIDate(new Date(now.getFullYear(), now.getMonth() + 1, 1)),
      data_fim: toAPIDate(new Date(now.getFullYear(), now.getMonth() + 2, 0)),
    }
  }
  if (periodFilter === "este-ano") {
    return {
      data_ini: `${now.getFullYear()}-01-01`,
      data_fim: `${now.getFullYear()}-12-31`,
    }
  }
  if (periodFilter === "customizado") {
    return {
      data_ini: startDateStr || undefined,
      data_fim: endDateStr || undefined,
    }
  }
  return {}
}

type ParamUpdates = {
  status?: string[]
  evType?: string
  period?: string
  startDate?: string
  endDate?: string
  page?: number
  q?: string
}

export function SubscriptionsByEvent() {
  const { showLoading, hideLoading } = useLoading()
  const { user } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [eventos, setEventos] = useState<EventoDataType[]>([])
  const [meta, setMeta] = useState<CalendarioMeta | null>(null)

  // --- Leitura dos filtros a partir da URL ---
  const rawStatuses = searchParams.getAll("status")
  // "_all" = sentinela para "usuário limpou tudo explicitamente" (não volta ao default)
  const inscriptionFilters: string[] = rawStatuses.includes("_all")
    ? []
    : rawStatuses.length > 0
      ? rawStatuses
      : ["pendentes"]

  const typeFilter = searchParams.get("evType") || "todos"
  const periodFilter = searchParams.get("period") || "todos"
  const startDateStr = searchParams.get("startDate") || ""
  const endDateStr = searchParams.get("endDate") || ""
  const page = parseInt(searchParams.get("page") || "1")
  const search = searchParams.get("q") || ""

  const showEncerrados = inscriptionFilters.includes("encerrados")

  // --- Escrita na URL preservando activeTab e demais params ---
  const updateParams = (updates: ParamUpdates, replace = false) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)

        if ("status" in updates) {
          next.delete("status")
          const toSave = updates.status!.length === 0 ? ["_all"] : updates.status!
          for (const s of toSave) next.append("status", s)
        }
        if ("evType" in updates) {
          if (!updates.evType || updates.evType === "todos") next.delete("evType")
          else next.set("evType", updates.evType)
        }
        if ("period" in updates) {
          if (!updates.period || updates.period === "todos") next.delete("period")
          else next.set("period", updates.period)
        }
        if ("startDate" in updates) {
          if (!updates.startDate) next.delete("startDate")
          else next.set("startDate", updates.startDate)
        }
        if ("endDate" in updates) {
          if (!updates.endDate) next.delete("endDate")
          else next.set("endDate", updates.endDate)
        }
        if ("page" in updates) {
          if (!updates.page || updates.page === 1) next.delete("page")
          else next.set("page", String(updates.page))
        }
        if ("q" in updates) {
          if (!updates.q) next.delete("q")
          else next.set("q", updates.q)
        }

        return next
      },
      { replace },
    )
  }

  // --- Handlers ---
  const handleInscriptionFiltersChange = (values: string[]) => {
    const willHaveEncerrados = values.includes("encerrados")
    updateParams({
      status: values,
      ...(willHaveEncerrados !== showEncerrados ? { page: 1 } : {}),
    })
  }

  const handlePeriodFilterChange = (value: string) => {
    updateParams({ period: value, page: 1 })
  }

  const handleStartDateChange = (date: Date | null) => {
    updateParams({ startDate: date ? toAPIDate(date) : "", page: 1 })
  }

  const handleEndDateChange = (date: Date | null) => {
    updateParams({ endDate: date ? toAPIDate(date) : "", page: 1 })
  }

  const handleSearchChange = (value: string) => {
    updateParams({ q: value }, true) // replace para não poluir o histórico
  }

  // --- Busca à API ---
  useEffect(() => {
    if (!user) return
    const periodDates = getPeriodDates(periodFilter, startDateStr, endDateStr)
    showLoading()
    getCalendarioAction({
      emp_id: user.empId,
      page,
      limit: PAGE_SIZE,
      ...(showEncerrados ? { encerrados: "true" } : {}),
      ...periodDates,
    })
      .then(({ data }) => {
        setEventos(data.data)
        setMeta(data.meta ?? null)
      })
      .catch(() => toast.danger("Erro ao carregar eventos"))
      .finally(hideLoading)
  }, [user, page, showEncerrados, periodFilter, startDateStr, endDateStr])

  // --- Filtros client-side (search e type aplicados sobre a página atual) ---
  const activeStatusFilters = inscriptionFilters.filter((f) => f !== "encerrados")

  const filteredEventos = eventos.filter((evento) => {
    const matchSearch = !search || evento.descricao.toLowerCase().includes(search.toLowerCase())
    const matchType =
      typeFilter === "todos" || evento.formato?.toLowerCase() === typeFilter.toLowerCase()
    const matchInsc =
      activeStatusFilters.length === 0 ||
      (activeStatusFilters.includes("pendentes") && parseInt(evento.pendentes as any) > 0) ||
      (activeStatusFilters.includes("aprovados") && parseInt(evento.autorizados as any) > 0) ||
      (activeStatusFilters.includes("recusados") && parseInt(evento.recusados as any) > 0)

    return matchSearch && matchInsc && matchType
  })

  const handleCardClick = (evento: EventoDataType) => {
    navigate(`/subscriptions/event/${evento.id}`, { state: { evento } })
  }

  const totalPages = meta?.totalPages ?? 1

  return (
    <div className="animate-fade-in space-y-6">
      <h1 className="text-2xl font-bold">Inscrições por Evento</h1>

      <SubscriptionsByEventFilter
        search={search}
        onSearchChange={handleSearchChange}
        inscriptionFilters={inscriptionFilters}
        onInscriptionFiltersChange={handleInscriptionFiltersChange}
        typeFilter={typeFilter}
        onTypeFilterChange={(v) => updateParams({ evType: v, page: 1 })}
        periodFilter={periodFilter}
        onPeriodFilterChange={handlePeriodFilterChange}
        startDate={startDateStr ? new Date(startDateStr + "T12:00:00") : null}
        onStartDateChange={handleStartDateChange}
        endDate={endDateStr ? new Date(endDateStr + "T12:00:00") : null}
        onEndDateChange={handleEndDateChange}
        defaultStartText={apiDateToDisplay(startDateStr)}
        defaultEndText={apiDateToDisplay(endDateStr)}
      />

      <div className="flex flex-col gap-3">
        {filteredEventos.map((evento) => {
          const autorizados = parseInt(evento.autorizados as any) || 0
          const recusados = parseInt(evento.recusados as any) || 0
          const pendentes = parseInt(evento.pendentes as any) || 0
          const total = autorizados + recusados + pendentes
          const progresso = total > 0 ? ((autorizados + recusados) / total) * 100 : 0

          return (
            <motion.div
              key={evento.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <EventCard
                evento={evento}
                progresso={progresso}
                autorizados={autorizados}
                recusados={recusados}
                pendentes={pendentes}
                currentOpened={null}
                onCardClick={handleCardClick}
                hideActions
              />
            </motion.div>
          )
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
            {meta && <span className="ml-2">({meta.total} eventos)</span>}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => updateParams({ page: page - 1 })}
            >
              <ChevronLeft className="size-4" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!meta?.hasMore}
              onClick={() => updateParams({ page: page + 1 })}
            >
              Próximo
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
